import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { Fade, Zoom } from "@mui/material";
import OrderDetailsSection from "./components/OrderDetailsSection";
import ThermalPrinterTest from "src/print-configuration/PrintConfigration";
import { useOrderDetails } from "./hooks/useOrderDetails";
import PaymentDetailsSection from "./components/PaymentDetailsSection";
import { PageTitle } from "src/shared/PageTitle";
import LoadingScreen from "src/shared/components/LoadingScreen";
import useResPrintData from "./useResPrintData";
import { Order } from "src/shared/services/interfacev2";
import { putOrder } from "src/shared/services/servicesv2";
import { pageTitle } from "src/shared/utils/brand";
import { notifyError, notifySuccess } from "src/assets/toastService";
import { processCardPayment } from "src/paymentGatway/useCardPayment";
import { useUser } from "src/shared/context/UserContext";

const Respayment: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userConfigration } = useUser();

  const [tip, setTip] = useState(0);
  const [customTip, setCustomTip] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPrint, setShowPrint] = useState(false);

  const { order, setOrder, loading, refetchOrder } = useOrderDetails(id);

  const printData = useResPrintData(order);

  useEffect(() => {
    document.title = pageTitle("Payment");
  }, []);

  const txn = order?.orders?.[0];

  const guest = txn?.guest || 0;
  const baseAmount = Number(txn?.finalAmount || 0);
  const total = baseAmount;
  const totalWithTip = baseAmount + tip;
  const finalAmount = baseAmount + tip;

  const handleCustomTipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomTip(value);
    setTip(Number(value) || 0);
  };

  const handleTipChange = (amount: number) => {
    setTip(amount);
    setCustomTip("");
  };

  const now = new Date();
  const localDate = now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0") + "-" + String(now.getDate()).padStart(2, "0");

  const handlePayNow = async () => {
    if (!order || !txn || !id) return;

    setIsProcessing(true);

    const paymentPayload = {
      customerId: order.orders[0].customerId,
      orderStatus: "Completed",
      finalAmount: finalAmount,
      remainingAmount: 0,
      outstandingBalance: totalWithTip,
      // outstandingBalance: 0,
      newOrderAmount: 0,
      orderDate: localDate,
      orderTime: now.toTimeString().split(" ")[0],
      date: localDate,
      time: now.toTimeString().split(" ")[0],
      payments: { paymentMethod, receivedAmount: totalWithTip },
      paymentHistory: [{ paymentMethod, receivedAmount: totalWithTip }],
      tip: tip, // ✅ Sending tip
      parcelFee: Number(order.orders[0].parcelFee ?? 0),
      deliveryFee: Number(order.orders[0].deliveryFee ?? 0),
      discount: Number(order.orders[0].discount ?? 0),
    };

    if (paymentPayload.payments.paymentMethod === "Card" && Boolean(userConfigration?.cardPayment) === true) {
      await processCardPayment({
        amount: paymentPayload.payments.receivedAmount,
        description: `Order payment for ${order?.orders[0]?.customerId || "Unknown Customer"}`,
        onSuccess: async (paymentDetails) => {
          const paysucess = {
            ...paymentPayload,
            paymentDetails,
          };
          try {
            await putOrder(id, paysucess);
            notifySuccess("Payment completed successfully!");

            // ✅ Refetch order to get the latest data including tip
            await refetchOrder();

            // Then update local state
            setOrder((prev): Order | null => {
              if (!prev) return null;
              return {
                ...prev,
                orders: prev.orders.map((o, i) => i === 0 ? { ...o, orderStatus: "Completed", remainingAmount: 0, tip: tip, } : o,),
                paymentHistory: [
                  ...(prev.paymentHistory || []),
                  {
                    _id: { $oid: "" },
                    paidOn: { $date: new Date().toISOString() },
                    paymentMethod,
                    paymentSource: "POS",
                    receivedAmount: total,
                    refId: txn.refId,
                  },
                ],
              };
            });

            setShowPrint(true);
            setTimeout(() => {
              setTimeout(() => {
                navigate("/v1/Reshome");
              }, 1000);
            }, 500);
          } catch (error) {
            console.error(error);
            notifyError("Failed to complete payment");
          } finally {
            setIsProcessing(false);
          }

        },
      });
      return;
    }

    try {
      await putOrder(id, paymentPayload);
      notifySuccess("Payment completed successfully!");

      // ✅ Refetch order to get the latest data including tip
      await refetchOrder();

      // Then update local state
      setOrder((prev) => {
        if (!prev) return null;

        return {
          ...prev,
          orders: prev.orders.map((o, i) => i === 0 ? { ...o, orderStatus: "Completed", remainingAmount: 0, tip: tip, } : o,),
          paymentHistory: [
            {
              ...(prev.paymentHistory?.[0] ?? {}),
              paymentMethod,
              paymentSource: "POS",
              receivedAmount: totalWithTip,
              paidOn: { $date: new Date().toISOString() },
              refId: txn.refId,
            },
          ],
        };
      });

      setShowPrint(true);
      setTimeout(() => {
        setTimeout(() => {
          navigate("/v1/Reshome");
        }, 1000);
      }, 500);
    } catch (error) {
      console.error(error);
      notifyError("Failed to complete payment");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEditConfirm = async ({ parcelFee, deliveryFee, discount }: { parcelFee: number; deliveryFee: number; discount: number }) => {
    if (!order || !id) return;

    const finalamount = Number(order.orders[0].orderAmount) + parcelFee + deliveryFee - discount;

    try {
      const payload = {
        customerId: order.orders[0].customerId,
        orderStatus: order.orders[0].status,
        parcelFee,
        deliveryFee,
        discount,
        remainingAmount: finalamount,
        finalAmount: finalamount,
      };

      await putOrder(id, payload);
      await refetchOrder();
      notifySuccess("Order updated successfully!");
    } catch (err) {
      console.error(err);
      notifyError("Failed to update order");
    }
  };

  return (
    <div className="flex flex-col h-[91.5vh] overflow-hidden">
      <PageTitle title="- Payment" />

      <Fade in={!loading} timeout={500}>
        <div className="flex flex-1 flex-col lg:flex-row p-1.5 gap-2 h-[calc(100vh-4rem)] overflow-hidden">
          <Zoom in={!loading} style={{ transitionDelay: "200ms" }}>
            <div className="w-full lg:w-[63%] h-full overflow-hidden rounded-2xl border border-gray-100">
              <OrderDetailsSection order={order} />
            </div>
          </Zoom>

          <div className="thin-scrollbar w-full lg:w-[37%] h-full overflow-y-auto">
            <PaymentDetailsSection
              order={order}
              tip={tip}
              customTip={customTip}
              paymentMethod={paymentMethod}
              guess={guest}
              total={totalWithTip}
              onTipChange={handleTipChange}
              onCustomTipChange={handleCustomTipChange}
              onPayNow={handlePayNow}
              onPaymentMethodChange={setPaymentMethod}
              isProcessing={isProcessing}
              onConfirmEdit={handleEditConfirm}
            />
          </div>
        </div>
      </Fade>

      {showPrint && printData && <ThermalPrinterTest PrintData={printData} isClosed={() => setShowPrint(false)} openPrint="true" />}

      <ToastContainer autoClose={2000} position="top-right" />
      {loading && <LoadingScreen />}
    </div>
  );
};

export default Respayment;
